import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, Clock, PauseCircle, Play, RefreshCw, type LucideIcon } from "lucide-react";
import { api, type SchedulerStatusResponse, type SweepEntryView } from "../../lib/api.js";
import { Skeleton } from "../../components/ui/Skeleton.js";

/**
 * Scheduler health card on the Sources page header.
 *
 * Shows:
 *   - Active/paused pill (per DISCOVERY_AUTOPOLL env switch)
 *   - "Next run in Xs" countdown + "last sweep Ys ago"
 *   - Per-sweep stats (feeds polled, items new, errors)
 *   - Mini-feed of the last 3 sweeps
 *   - "Run now" button to force a sweep
 */
export function SchedulerStatusCard() {
  const qc = useQueryClient();
  const { data, isPending } = useQuery({
    queryKey: ["discovery", "scheduler"],
    queryFn: () => api.getDiscoveryScheduler(),
    refetchInterval: 5_000,
  });

  const runNow = useMutation({
    mutationFn: () => api.runDiscoveryScheduler(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["discovery"] });
    },
  });

  if (isPending || !data) {
    return (
      <div className="card p-4">
        <Skeleton className="mb-2 h-4 w-32" />
        <Skeleton className="h-3 w-48" />
      </div>
    );
  }

  const s = data.scheduler;
  const h = data.health;

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <StatusDot enabled={s.enabled} failing={h.failingSources > 0} inFlight={s.inFlight} />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-ink">
                {s.enabled ? "Auto-poll active" : "Auto-poll paused"}
              </span>
              {!s.enabled && s.reason && (
                <span className="text-2xs text-muted">({s.reason})</span>
              )}
              {s.inFlight && (
                <span className="inline-flex items-center gap-1 text-2xs text-info">
                  <RefreshCw className="h-3 w-3 animate-spin" />
                  sweeping…
                </span>
              )}
            </div>
            <div className="mt-0.5 text-2xs text-muted">
              {s.enabled ? (
                <>
                  <span className="font-mono">{s.cronExpression}</span>
                  {s.nextRunAt && (
                    <>
                      {" "}· next run {relativeTimeToFuture(s.nextRunAt)}
                    </>
                  )}
                  {s.lastSweep && (
                    <>
                      {" "}· last sweep {relativeTimePast(s.lastSweep.finishedAt)}
                    </>
                  )}
                  {" "}· {s.totalSweeps} total
                </>
              ) : (
                "Sweeps disabled. Run manually or set DISCOVERY_AUTOPOLL=on."
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <HealthDigits label="active" value={h.activeSources} variant="ok" />
          <HealthDigits label="paused" value={h.pausedSources} variant="muted" />
          <HealthDigits label="failing" value={h.failingSources} variant={h.failingSources > 0 ? "danger" : "muted"} />
          <button
            onClick={() => runNow.mutate()}
            disabled={runNow.isPending || s.inFlight}
            className="btn-secondary btn-sm gap-1.5"
            title="Force a sweep now"
          >
            {runNow.isPending ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            Run now
          </button>
        </div>
      </div>

      {s.recentSweeps.length > 0 && (
        <div className="border-t border-line bg-canvas-deep/40 px-4 py-2.5">
          <div className="mb-1.5 text-2xs font-semibold uppercase tracking-wider text-muted">Recent sweeps</div>
          <ul className="space-y-1">
            {s.recentSweeps.slice(0, 3).map((sw, i) => (
              <SweepLine key={i} sweep={sw} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function StatusDot({ enabled, failing, inFlight }: { enabled: boolean; failing: boolean; inFlight: boolean }) {
  if (!enabled) {
    return (
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-line-soft text-muted-dark">
        <PauseCircle className="h-4 w-4" strokeWidth={2.2} />
      </div>
    );
  }
  if (failing) {
    return (
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-danger-soft text-red-700">
        <AlertCircle className="h-4 w-4" strokeWidth={2.2} />
      </div>
    );
  }
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-emerald-700">
      {inFlight ? <RefreshCw className="h-4 w-4 animate-spin" strokeWidth={2.2} /> : <CheckCircle2 className="h-4 w-4" strokeWidth={2.2} />}
    </div>
  );
}

function HealthDigits({ label, value, variant }: { label: string; value: number; variant: "ok" | "muted" | "danger" }) {
  const colour =
    variant === "ok" ? "text-emerald-700" :
    variant === "danger" ? "text-red-700" :
    "text-muted";
  return (
    <div className="text-right">
      <div className={`text-base font-semibold leading-none ${colour}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted">{label}</div>
    </div>
  );
}

function SweepLine({ sweep }: { sweep: SweepEntryView }) {
  const failed = sweep.failedFeedIds.length > 0;
  const IconCmp: LucideIcon = failed ? AlertCircle : sweep.feedsPolled === 0 ? Clock : CheckCircle2;
  return (
    <li className="flex items-center justify-between gap-2 text-2xs">
      <span className="inline-flex items-center gap-1.5 text-muted">
        <IconCmp className={`h-3 w-3 ${failed ? "text-red-700" : "text-emerald-700"}`} strokeWidth={2.4} />
        <span className="font-mono">{relativeTimePast(sweep.startedAt)}</span>
        <span>·</span>
        <span>
          {sweep.feedsPolled === 0
            ? "no feeds due"
            : `${sweep.feedsPolled} feed${sweep.feedsPolled === 1 ? "" : "s"} polled`}
        </span>
      </span>
      {sweep.feedsPolled > 0 && (
        <span className="text-muted">
          +{sweep.itemsNew} new of {sweep.itemsSeen} seen
          {sweep.itemsAnalyzed > 0 && ` · ${sweep.itemsAnalyzed} analyzed`}
          {failed && <span className="ml-1 text-red-700">· {sweep.failedFeedIds.length} failed</span>}
          <span className="ml-1 text-muted/70">({sweep.durationMs}ms)</span>
        </span>
      )}
    </li>
  );
}

function relativeTimePast(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function relativeTimeToFuture(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  if (diff < 0) return "now";
  const s = Math.ceil(diff / 1000);
  if (s < 60) return `in ${s}s`;
  const m = Math.ceil(s / 60);
  return `in ${m}m`;
}

// Silence unused-import diagnostic — SchedulerStatusResponse type retained for callers.
export const _schedulerStatusType = null as unknown as SchedulerStatusResponse;
